<template>
  <div class="page-wrap">
    <div v-if="loaded">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="page-header__left">
          <button class="btn-back" @click="$router.back()">
            <i class="fa fa-arrow-left"></i>
          </button>
          <div>
            <h1 class="page-title">User #{{ user.id }}</h1>
            <div class="page-meta">
              <span class="meta-item"><i class="fa fa-envelope"></i> {{ user.email }}</span>
              <span class="meta-item" v-if="user.phone_number"><i class="fa fa-phone"></i> {{ user.phone_number }}</span>
              <span class="meta-item"><i class="fa fa-venus-mars"></i> {{ genderForHuman }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Quick Actions ── -->
      <div class="actions-bar">
        <button class="action-btn action-btn--primary" @click="loginAsUser()">
          <i class="fa fa-sign-in"></i> Login as User
        </button>
        <button class="action-btn action-btn--blue" @click="showAddOrDeductCreditModal()">
          <i class="fa fa-credit-card"></i> Add / Deduct Credit
        </button>
        <button class="action-btn action-btn--secondary" @click="$router.push(`/users/${id}/purchases`)">
          <i class="fa fa-history"></i> Purchase History
        </button>
        <button class="action-btn action-btn--secondary" @click="$router.push(`/users/${id}/wallet-history`)">
          <i class="fa fa-wallet"></i> Credit History
        </button>
      </div>

      <!-- ── Edit Form ── -->
      <div class="panel">
        <div class="panel-title"><i class="fa fa-user-edit"></i> Edit User Info</div>

        <div class="form-grid">
          <div class="form-field">
            <label class="filter-label">First Name</label>
            <input v-model="updateForm.first_name" class="dd-input" placeholder="First name" />
          </div>
          <div class="form-field">
            <label class="filter-label">Last Name</label>
            <input v-model="updateForm.last_name" class="dd-input" placeholder="Last name" />
          </div>
          <div class="form-field">
            <label class="filter-label">Email</label>
            <input v-model="updateForm.email" class="dd-input" placeholder="email@example.com" />
          </div>
          <div class="form-field">
            <label class="filter-label">Phone Number</label>
            <input v-model="updateForm.phone_number" class="dd-input" placeholder="+971..." />
          </div>
          <div class="form-field">
            <label class="filter-label">Whatsapp Number</label>
            <input v-model="updateForm.whatsapp_number" class="dd-input" placeholder="+971..." />
          </div>
          <div class="form-field">
            <label class="filter-label">Telegram Number</label>
            <input v-model="updateForm.telegram_number" class="dd-input" placeholder="@username" />
          </div>
          <div class="form-field">
            <label class="filter-label">Gender</label>
            <select v-model="updateForm.gender" class="dd-input">
              <option value="1">Man</option>
              <option value="2">Woman</option>
              <option value="3">Unknown</option>
            </select>
          </div>
          <div class="form-field">
            <label class="filter-label">Birthday</label>
            <input v-model="updateForm.birth_date" type="date" class="dd-input" />
          </div>
        </div>

        <ul class="error-list" v-if="errors.length">
          <li v-for="error in errors" :key="error">{{ error }}</li>
        </ul>

        <div class="form-actions">
          <button class="btn-reset" @click="resetForm">
            <i class="fa fa-undo"></i> Reset
          </button>
          <button class="btn-save" @click="updateUser">
            <i class="fa fa-check"></i> Save Changes
          </button>
        </div>
      </div>

      <!-- ── Addresses ── -->
      <div class="panel">
        <div class="panel-title-row">
          <div class="panel-title"><i class="fa fa-map-marker"></i> Addresses</div>
          <button class="btn-add" @click="$modal.show('create-address-modal')">
            <i class="fa fa-plus"></i> New Address
          </button>
        </div>

        <div class="table-responsive-wrap">
          <table class="dd-table" v-if="addressesLoaded && addresses.length">
            <thead>
            <tr>
              <th>Country</th>
              <th>City</th>
              <th>Address</th>
              <th>Postcode</th>
              <th>Default</th>
              <th>Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="address in addresses" :key="address.id" class="dd-table__row">
              <td>{{ address.country.name }}</td>
              <td>{{ address.city.name }}</td>
              <td>{{ address.address }}</td>
              <td>{{ address.post_code || '—' }}</td>
              <td>
                  <span :class="address.is_default ? 'default-badge default-badge--yes' : 'default-badge default-badge--no'">
                    {{ address.is_default ? 'Default' : 'No' }}
                  </span>
              </td>
              <td>
                <div class="td-actions">
                  <button class="btn-icon btn-icon--blue" @click="showEditAddressModal(address)" title="Edit">
                    <i class="fa fa-pencil"></i>
                  </button>
                  <button class="btn-icon btn-icon--red" @click="deleteAddress(address.id)" title="Delete">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
          <div class="empty-state" v-else-if="addressesLoaded">
            <i class="fa fa-map-o"></i>
            <div>No addresses found</div>
          </div>
        </div>
      </div>

    </div>

    <!-- Loading -->
    <div class="mt-5 pt-5">
      <loading v-if="!loaded"></loading>
    </div>

    <!-- ── Create Address Modal ── -->
    <modal name="create-address-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">New Address</span>
          <button class="dd-modal__close" @click="$modal.hide('create-address-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">Country</label>
              <country-search-box @selected="(id) => createAddressForm.country_id = id"></country-search-box>
            </div>
            <div class="form-field">
              <label class="filter-label">City</label>
              <city-search-box @selected="(id) => createAddressForm.city_id = id"></city-search-box>
            </div>
            <div class="form-field form-field--full">
              <label class="filter-label">Address</label>
              <input v-model="createAddressForm.address" class="dd-input" placeholder="Street address" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Postcode</label>
              <input v-model="createAddressForm.post_code" class="dd-input" placeholder="Postcode" />
            </div>
            <div class="form-field">
              <label class="filter-label">Is Default?</label>
              <select v-model="createAddressForm.is_default" class="dd-input">
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
          </div>
          <ul class="error-list" v-if="createAddressForm.errors && createAddressForm.errors.length">
            <li v-for="error in createAddressForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('create-address-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="createAddress">
            <i class="fa fa-plus"></i> Create
          </button>
        </div>
      </div>
    </modal>

    <!-- ── Edit Address Modal ── -->
    <modal name="edit-address-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Edit Address</span>
          <button class="dd-modal__close" @click="$modal.hide('edit-address-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="filter-label">Country</label>
              <country-search-box
                  :name="editAddressForm.country ? editAddressForm.country.name : '—'"
                  @selected="(id) => editAddressForm.country_id = id"
              ></country-search-box>
            </div>
            <div class="form-field">
              <label class="filter-label">City</label>
              <city-search-box
                  :name="editAddressForm.city ? editAddressForm.city.name : '—'"
                  @selected="(id) => editAddressForm.city_id = id"
              ></city-search-box>
            </div>
            <div class="form-field form-field--full">
              <label class="filter-label">Address</label>
              <input v-model="editAddressForm.address" class="dd-input" placeholder="Street address" required />
            </div>
            <div class="form-field">
              <label class="filter-label">Postcode</label>
              <input v-model="editAddressForm.post_code" class="dd-input" placeholder="Postcode" />
            </div>
            <div class="form-field checkbox-field">
              <label class="checkbox-label">
                <input type="checkbox" v-model="editAddressForm.is_default" class="checkbox-input" />
                <span>Set as default address</span>
              </label>
            </div>
          </div>
          <ul class="error-list" v-if="editAddressForm.errors && editAddressForm.errors.length">
            <li v-for="error in editAddressForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('edit-address-modal')">Cancel</button>
          <button class="btn-submit" @click.prevent="editAddress">
            <i class="fa fa-check"></i> Save
          </button>
        </div>
      </div>
    </modal>

    <!-- ── Credit Modal ── -->
    <modal name="change-credit-modal" height="auto">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Customer Credits Management</span>
          <button class="dd-modal__close" @click="$modal.hide('change-credit-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <div class="credit-balance">
            <div class="credit-balance__label">Available Credit</div>
            <div class="credit-balance__val">{{ user.balance }} AED</div>
          </div>
          <b-tabs class="dd-tabs">
            <b-tab title="Add Credit" active>
              <div class="form-field mt-3">
                <label class="filter-label">Amount to Add</label>
                <input v-model="changeCreditForm.amount" class="dd-input" type="number" placeholder="0.00" />
              </div>
              <div class="form-field mt-3">
                <label class="filter-label">Admin Password</label>
                <input v-model="changeCreditForm.password" class="dd-input" type="password" placeholder="••••••••" />
              </div>
              <button class="btn-submit btn-submit--green w-100 mt-3" @click.prevent="changeCredit('deposit')">
                <i class="fa fa-plus"></i> Add Credit
              </button>
            </b-tab>
            <b-tab title="Deduct Credit">
              <div class="form-field mt-3">
                <label class="filter-label">Amount to Deduct</label>
                <input v-model="changeCreditForm.amount" class="dd-input" type="number" placeholder="0.00" />
              </div>
              <div class="form-field mt-3">
                <label class="filter-label">Admin Password</label>
                <input v-model="changeCreditForm.password" class="dd-input" type="password" placeholder="••••••••" />
              </div>
              <button class="btn-submit btn-submit--red w-100 mt-3" @click.prevent="changeCredit('withdraw')">
                <i class="fa fa-minus"></i> Deduct Credit
              </button>
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </modal>

  </div>
</template>

<script>
import { config } from "../../helpers";
import CountrySearchBox from "../../components/CountrySearchBox.vue";
import CitySearchBox from "../../components/CitySearchBox.vue";

export default {
  name: "User",
  components: {
    "country-search-box": CountrySearchBox,
    "city-search-box": CitySearchBox,
  },
  data() {
    return {
      id: this.$route.params.id,
      loaded: false,
      user: null,
      updateForm: {},
      errors: [],
      addressesLoaded: false,
      addresses: [],
      createAddressForm: {},
      editAddressForm: {},
      changeCreditForm: { amount: 0.0, password: "" },
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/users";
    this.loadData();
    this.loadAddresses();
  },

  methods: {
    loadData() {
      axios.get(`${this.url}/${this.id}`).then((r) => {
        this.user       = r.data.data;
        this.updateForm = JSON.parse(JSON.stringify(r.data.data));
        this.loaded     = true;
      });
    },

    loadAddresses() {
      axios.get(`${this.url}/${this.id}/addresses`).then((r) => {
        this.addresses        = r.data.data;
        this.addressesLoaded  = true;
      });
    },

    updateUser(event) {
      event.preventDefault();
      axios
          .put(`${this.url}/${this.updateForm.id}`, this.updateForm)
          .then(() => location.reload())
          .catch((e) => { this.errors = Object.values(e.response.data.errors).flat(); });
    },

    resetForm(event) {
      event.preventDefault();
      this.updateForm = { ...this.user };
    },

    deleteAddress(addressId) {
      if (!confirm("Do you really want to delete this address?")) return;
      axios.delete(`${this.url}/${this.id}/addresses/${addressId}`).then(() => this.loadAddresses());
    },

    createAddress() {
      axios
          .post(`${this.url}/${this.id}/addresses`, this.createAddressForm)
          .then(() => {
            this.createAddressForm = {};
            alert("Address created successfully!");
            this.$modal.hide("create-address-modal");
            this.loadAddresses();
          })
          .catch((e) => { this.createAddressForm.errors = Object.values(e.response.data.errors).flat(); });
    },

    showEditAddressModal(address) {
      this.editAddressForm = JSON.parse(JSON.stringify(address));
      this.$modal.show("edit-address-modal");
    },

    editAddress() {
      axios
          .put(`${this.url}/${this.id}/addresses/${this.editAddressForm.id}`, this.editAddressForm)
          .then(() => {
            this.editAddressForm = {};
            alert("Address updated successfully!");
            this.$modal.hide("edit-address-modal");
            this.loadAddresses();
          })
          .catch((e) => { this.editAddressForm.errors = Object.values(e.response.data.errors).flat(); });
    },

    showAddOrDeductCreditModal() {
      this.changeCreditForm = { amount: 0.0, password: "" };
      this.$modal.show("change-credit-modal");
    },

    changeCredit(action) {
      axios
          .post(`${this.url}/${this.id}/wallet/${action}`, this.changeCreditForm)
          .then(() => {
            this.$modal.hide("change-credit-modal");
            this.$router.push(`/users/${this.id}/wallet-history`);
          })
          .catch((e) => {
            alert(Object.values(e.response?.data?.errors ?? ["Something went wrong!"]).flat()[0]);
          });
    },

    loginAsUser() {
      axios.post(`${config("BACKEND_ROOT")}/admin/users/${this.id}/login`).then((r) => {
        const { access_token: token, refresh_token: refresh, cart } = r.data;
        window.open(
            `${config("FRONTEND_ROOT")}/auth/autologin?token=${token}&refresh=${refresh}&uuid=${cart.uuid}&count=${cart.items.length}&to=/dashboard`,
            "_blank"
        ).focus();
      });
    },
  },

  computed: {
    genderForHuman() {
      const map = { 1: "Man", 2: "Woman", 3: "Unknown" };
      return map[this.user?.gender] ?? "—";
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 6px; }
.page-meta   { display: flex; flex-wrap: wrap; gap: 14px; }
.meta-item   { font-size: 12px; color: var(--text-3); display: flex; align-items: center; gap: 5px; }

/* ── Actions bar ── */
.actions-bar { display: flex; gap: 10px; flex-wrap: wrap; }
.action-btn { height: 38px; padding: 0 16px; border-radius: 9px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: opacity .15s, transform .1s; border: 1px solid transparent; }
.action-btn:active { transform: scale(.98); }
.action-btn--primary   { background: var(--accent); color: #fff; border-color: var(--accent); }
.action-btn--primary:hover { opacity: .88; }
.action-btn--blue      { background: rgba(129,140,248,.12); color: #818cf8; border-color: rgba(129,140,248,.3); }
.action-btn--blue:hover { background: rgba(129,140,248,.2); }
.action-btn--secondary { background: var(--bg-hover); color: var(--text-2); border-color: var(--border); }
.action-btn--secondary:hover { border-color: var(--border-2); color: var(--text-1); }

/* ── Panel ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.1rem; display: flex; align-items: center; gap: 7px; }
.panel-title-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.1rem; }
.panel-title-row .panel-title { margin-bottom: 0; }

/* ── Form grid ── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.form-field { display: flex; flex-direction: column; gap: 5px; }
.form-field--full { grid-column: span 2; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }

/* checkbox */
.checkbox-field { justify-content: flex-end; padding-top: 8px; }
.checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: var(--text-2); }
.checkbox-input { accent-color: var(--accent); width: 15px; height: 15px; cursor: pointer; }

.error-list { margin-top: 10px; padding-left: 16px; color: var(--accent); font-size: 13px; }

.form-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid var(--border); }
.btn-reset { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s; }
.btn-reset:hover { border-color: var(--border-2); }
.btn-save  { height: 36px; padding: 0 20px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-save:hover { opacity: .88; }

/* ── Address table ── */
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; white-space: nowrap; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.default-badge { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; }
.default-badge--yes { background: rgba(34,197,94,.12); color: #22c55e; border: 1px solid rgba(34,197,94,.25); }
.default-badge--no  { background: var(--bg-hover); color: var(--text-3); border: 1px solid var(--border); }

.td-actions { display: flex; gap: 6px; }
.btn-icon { width: 30px; height: 30px; border-radius: 7px; border: 1px solid var(--border); background: var(--bg-hover); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 13px; color: var(--text-2); transition: border-color .15s, color .15s, background .15s; }
.btn-icon--blue:hover { border-color: #818cf8; color: #818cf8; background: rgba(129,140,248,.08); }
.btn-icon--red:hover  { border-color: var(--accent); color: var(--accent); background: rgba(244,47,75,.08); }

.btn-add { height: 32px; padding: 0 12px; background: rgba(34,197,94,.1); border: 1px solid rgba(34,197,94,.25); border-radius: 8px; color: #22c55e; font-size: 12px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background .15s; }
.btn-add:hover { background: rgba(34,197,94,.18); }

.empty-state { text-align: center; padding: 2.5rem 1rem; color: var(--text-3); }
.empty-state i { font-size: 28px; display: block; margin-bottom: 8px; }

/* ── Modals ── */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }

.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }
.btn-submit--green { background: #16a34a; }
.btn-submit--red   { background: #f42f4b; }
.w-100 { width: 100% !important; }
.mt-3  { margin-top: 1rem; }

/* Credit modal */
.credit-balance { background: var(--bg-hover); border: 1px solid var(--border); border-radius: 10px; padding: 12px 16px; margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; }
.credit-balance__label { font-size: 12px; color: var(--text-3); }
.credit-balance__val   { font-size: 18px; font-weight: 600; color: #22c55e; }

.dd-tabs >>> .nav-pills .nav-link { background: var(--bg-hover); border: 1px solid var(--border); color: var(--text-2); font-size: 13px; border-radius: 8px; margin-right: 6px; }
.dd-tabs >>> .nav-pills .nav-link.active { background: var(--accent); border-color: var(--accent); color: #fff; }

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 768px) {
  .form-grid { grid-template-columns: 1fr; }
  .form-field--full { grid-column: span 1; }
  .actions-bar { flex-direction: column; }
  .action-btn { width: 100%; justify-content: center; }
}
</style>